import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Plus, Edit3, Truck } from 'lucide-react';
import api from '../api';

const S = `
.sl-wrap{color:#0f172a}
.sl-hero{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;margin-bottom:20px;padding:20px 22px;border-radius:20px;background:linear-gradient(135deg,#ecfeff 0%,#ffffff 55%,#f8fafc 100%);border:1px solid #bae6fd;box-shadow:0 16px 40px rgba(15,23,42,.08)}
.sl-title{margin:12px 0 6px;font-size:24px;font-weight:800}
.sl-subtitle{margin:0;color:#475569;font-size:14px;line-height:1.6;max-width:760px}
.sl-eyebrow{display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border-radius:999px;background:rgba(8,145,178,.1);color:#0e7490;font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
.sl-stat-grid{display:grid;grid-template-columns:repeat(3,minmax(120px,1fr));gap:12px;min-width:min(100%,360px)}
.sl-stat{padding:14px 16px;border-radius:16px;background:#fff;border:1px solid #e2e8f0;box-shadow:0 10px 24px rgba(15,23,42,.06)}
.sl-stat-label{font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#0891b2}
.sl-stat-value{margin-top:6px;font-size:20px;font-weight:800}
.sl-toolbar{display:flex;gap:12px;flex-wrap:wrap;align-items:center;margin-bottom:16px;padding:16px 18px;background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 10px 30px rgba(15,23,42,.06)}
.sl-search{position:relative;flex:1;min-width:240px}
.sl-input,.sl-select,.sl-textarea{border:1px solid #cbd5e1;border-radius:12px;padding:11px 12px;background:#fff;color:#0f172a;font-size:13px;box-shadow:0 1px 2px rgba(15,23,42,.04);width:100%;box-sizing:border-box}
.sl-textarea{resize:vertical;min-height:92px}
.sl-input:focus,.sl-select:focus,.sl-textarea:focus{outline:none;border-color:#0891b2;box-shadow:0 0 0 4px rgba(8,145,178,.12)}
.sl-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:1px solid #0f766e;background:#0f766e;color:#fff;border-radius:12px;padding:11px 16px;font-size:13px;font-weight:700;cursor:pointer;min-height:42px}
.sl-btn:disabled,.sl-ghost:disabled{opacity:.55;cursor:not-allowed}
.sl-card{background:#fff;border:1px solid #e2e8f0;border-radius:20px;box-shadow:0 16px 40px rgba(15,23,42,.06);overflow:hidden}
.sl-card-head{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:18px 20px;border-bottom:1px solid #e2e8f0;background:linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)}
.sl-table-wrap{overflow:auto}
.sl-table{width:100%;border-collapse:collapse;font-size:13px;min-width:1100px}
.sl-table th{background:#f8fafc;color:#334155;text-align:left;padding:13px 16px;border-bottom:1px solid #e2e8f0;white-space:nowrap;font-size:12px;font-weight:800;text-transform:uppercase}
.sl-table td{padding:14px 16px;border-bottom:1px solid #f1f5f9;white-space:nowrap;vertical-align:middle}
.sl-name{display:flex;flex-direction:column;gap:2px}
.sl-name strong{font-size:13px}
.sl-name span{font-size:12px;color:#64748b}
.sl-empty{padding:36px 20px;text-align:center;color:#475569}
.sl-actions{display:flex;gap:8px}
.sl-ghost{display:inline-flex;align-items:center;justify-content:center;gap:6px;border:1px solid #cbd5e1;background:#fff;color:#0f172a;border-radius:10px;padding:8px 11px;font-size:12px;font-weight:600;cursor:pointer}
.sl-modal{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:1200;display:flex;align-items:center;justify-content:center;padding:16px}
.sl-modal-card{width:min(100%,980px);max-width:980px;max-height:calc(100vh - 32px);background:#fff;border-radius:18px;border:1px solid #cbd5e1;box-shadow:0 28px 70px rgba(15,23,42,.28);overflow:hidden;display:flex;flex-direction:column}
.sl-modal-h{padding:16px 18px;border-bottom:1px solid #dbeafe;font-weight:800}
.sl-modal-b{padding:14px 16px;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px;overflow-y:auto}
.sl-modal-f{padding:14px 16px;border-top:1px solid #e5e7eb;display:flex;justify-content:flex-end;gap:8px}
.sl-section{grid-column:1/-1;margin-top:6px;padding:14px;border:1px solid #e2e8f0;border-radius:16px;background:#f8fafc}
.sl-section h3{margin:0 0 4px;font-size:14px;font-weight:800}
.sl-section p{margin:0 0 12px;font-size:12px;color:#64748b}
.sl-section-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:10px}
@media(max-width:768px){.sl-hero{flex-direction:column}.sl-stat-grid{grid-template-columns:1fr}.sl-modal-card{width:100vw;height:100vh;border-radius:0}.sl-modal-b,.sl-section-grid{grid-template-columns:1fr}}
`;

function SupplierForm({ open, onClose, onSaved, edit }) {
  const emptyForm = {
    name: '', contact_person: '', phone: '', email: '', supplier_category: '', material_specialization: '',
    gstin: '', pan: '', address_line1: '', address_line2: '', city: '', state: '', pincode: '',
    payment_terms: 'IMMEDIATE', credit_days: '', bank_name: '', bank_account_no: '', bank_ifsc: '', notes: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [states, setStates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm,
      ...edit,
      phone: edit?.phone || '',
      email: edit?.email || '',
      credit_days: edit?.credit_days ?? '',
    });
  }, [open, edit]);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    api.get('/sales/states').then((res) => mounted && setStates(res?.data?.data || [])).catch(() => mounted && setStates([]));
    return () => { mounted = false; };
  }, [open]);

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = { ...form, credit_days: form.credit_days === '' ? 0 : Number(form.credit_days) };
      if (edit?.id) await api.put(`/sales/suppliers/${edit.id}`, payload);
      else await api.post('/sales/suppliers', payload);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sl-modal" onClick={onClose}>
      <div className="sl-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="sl-modal-h">{edit ? 'Edit Supplier' : 'Create Supplier'}</div>
        <div className="sl-modal-b">
          <input className="sl-input" placeholder="Supplier Name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          <input className="sl-input" placeholder="Contact Person" value={form.contact_person} onChange={(e) => set('contact_person', e.target.value)} />
          <input className="sl-input" placeholder="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          <input className="sl-input" placeholder="Email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          <select className="sl-select" value={form.supplier_category} onChange={(e) => set('supplier_category', e.target.value)}>
            <option value="">Select Supplier Category</option>
            <option value="GENERAL">GENERAL</option>
            <option value="METAL">METAL</option>
            <option value="STONE">STONE</option>
            <option value="PACKAGING">PACKAGING</option>
            <option value="MACHINERY">MACHINERY</option>
            <option value="SERVICES">SERVICES</option>
          </select>
          <input className="sl-input" placeholder="Material Specialization" value={form.material_specialization} onChange={(e) => set('material_specialization', e.target.value)} />
          <input className="sl-input" placeholder="GSTIN" value={form.gstin} onChange={(e) => set('gstin', e.target.value)} />
          <input className="sl-input" placeholder="PAN" value={form.pan} onChange={(e) => set('pan', e.target.value)} />
          <div className="sl-section">
            <h3>Address</h3>
            <p>Collect the business location and billing/contact address for the supplier.</p>
            <div className="sl-section-grid">
              <input className="sl-input" placeholder="Address Line 1" value={form.address_line1} onChange={(e) => set('address_line1', e.target.value)} />
              <input className="sl-input" placeholder="Address Line 2" value={form.address_line2} onChange={(e) => set('address_line2', e.target.value)} />
              <input className="sl-input" placeholder="City" value={form.city} onChange={(e) => set('city', e.target.value)} />
              <select className="sl-select" value={form.state} onChange={(e) => set('state', e.target.value)}>
                <option value="">Select State</option>
                {states.map((state) => <option key={state.id} value={state.name}>{state.name}</option>)}
              </select>
              <input className="sl-input" placeholder="Pincode" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} />
            </div>
          </div>
          <div className="sl-section">
            <h3>Commercial Details</h3>
            <p>Useful supplier master fields for payment follow-up and future purchase workflows.</p>
            <div className="sl-section-grid">
              <select className="sl-select" value={form.payment_terms} onChange={(e) => set('payment_terms', e.target.value)}>
                <option value="IMMEDIATE">IMMEDIATE</option>
                <option value="NET_7">NET_7</option>
                <option value="NET_15">NET_15</option>
                <option value="NET_30">NET_30</option>
                <option value="NET_45">NET_45</option>
              </select>
              <input className="sl-input" type="number" min="0" placeholder="Credit Days" value={form.credit_days} onChange={(e) => set('credit_days', e.target.value)} />
              <input className="sl-input" placeholder="Bank Name" value={form.bank_name} onChange={(e) => set('bank_name', e.target.value)} />
              <input className="sl-input" placeholder="Bank Account No" value={form.bank_account_no} onChange={(e) => set('bank_account_no', e.target.value)} />
              <input className="sl-input" placeholder="IFSC" value={form.bank_ifsc} onChange={(e) => set('bank_ifsc', e.target.value)} />
              <textarea className="sl-textarea" placeholder="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="sl-modal-f">
          <button className="sl-ghost" onClick={onClose}>Cancel</button>
          <button className="sl-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Supplier'}</button>
        </div>
      </div>
    </div>
  );
}

export default function SupplierList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/sales/suppliers', { params });
      setRows(res?.data?.data || []);
      setTotal(Number(res?.data?.total || 0));
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);
  const activeCount = useMemo(() => rows.filter((row) => row.is_active).length, [rows]);
  const gstCount = useMemo(() => rows.filter((row) => row.gstin).length, [rows]);

  return (
    <>
      <style>{S}</style>
      <div className="sl-wrap">
        <div className="sl-hero">
          <div>
            <div className="sl-eyebrow"><Truck size={12} /> Supplier Master</div>
            <h1 className="sl-title">Suppliers</h1>
            <p className="sl-subtitle">Maintain supplier master data for metals, stones, packaging, services, and other vendor relationships used by the sales team.</p>
          </div>
          <div className="sl-stat-grid">
            <div className="sl-stat"><div className="sl-stat-label">Visible Suppliers</div><div className="sl-stat-value">{rows.length}</div></div>
            <div className="sl-stat"><div className="sl-stat-label">GST Available</div><div className="sl-stat-value">{gstCount}</div></div>
            <div className="sl-stat"><div className="sl-stat-label">Active Suppliers</div><div className="sl-stat-value">{activeCount}</div></div>
          </div>
        </div>
        <div className="sl-toolbar">
          <div className="sl-search">
            <Search size={14} style={{ position: 'absolute', left: 10, top: 12, color: '#6b7280' }} />
            <input className="sl-input" style={{ paddingLeft: 30 }} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search supplier by name, phone, email, GSTIN" />
          </div>
          <button className="sl-btn" onClick={() => { setEditRow(null); setModalOpen(true); }}><Plus size={14} />Add Supplier</button>
        </div>
        <div className="sl-card">
          <div className="sl-card-head">
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Supplier Directory</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>{total.toLocaleString('en-IN')} total suppliers matched your current filters.</p>
            </div>
          </div>
          <div className="sl-table-wrap">
            <table className="sl-table">
              <thead>
                <tr>
                  <th>Supplier</th><th>Contact</th><th>Category</th><th>Specialization</th><th>Address</th><th>Payment Terms</th><th>Credit Days</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="sl-empty">Loading suppliers...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="sl-empty">No suppliers found</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id}>
                    <td><div className="sl-name"><strong>{r.name}</strong><span>{r.code || '-'} {r.gstin ? `| ${r.gstin}` : ''}</span></div></td>
                    <td><div className="sl-name"><strong>{r.contact_person || '-'}</strong><span>{r.phone || '-'} {r.email ? `| ${r.email}` : ''}</span></div></td>
                    <td>{r.supplier_category || '-'}</td>
                    <td>{r.material_specialization || '-'}</td>
                    <td>{r.full_address || '-'}</td>
                    <td>{r.payment_terms || '-'}</td>
                    <td>{r.credit_days || 0}</td>
                    <td><div className="sl-actions"><button className="sl-ghost" onClick={() => { setEditRow(r); setModalOpen(true); }}><Edit3 size={13} />Edit</button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 18px' }}>
            <div style={{ fontSize: 12, color: '#475569' }}>Page {page} of {pages}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sl-ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</button>
              <button className="sl-ghost" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next</button>
            </div>
          </div>
        </div>
      </div>
      <SupplierForm open={modalOpen} edit={editRow} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
    </>
  );
}
